export const createAbortError = () => new DOMException('The operation was aborted.', 'AbortError');

export const isAbortError = (error: unknown): boolean => error instanceof DOMException && error.name === 'AbortError';

export const throwIfAborted = (signal?: AbortSignal) => {
  if (signal?.aborted) throw createAbortError();
};

export const abortableDelay = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) return reject(createAbortError());

    const onAbort = () => {
      clearTimeout(timer);
      reject(createAbortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    signal?.addEventListener('abort', onAbort, { once: true });
  });

export interface RetryOptions {
  retries: number;
  baseDelayMs: number;
  signal?: AbortSignal;
}

export const withRetry = async <T>(task: () => Promise<T>, { retries, baseDelayMs, signal }: RetryOptions): Promise<T> => {
  for (let attempt = 0; ; attempt++) {
    throwIfAborted(signal);
    try {
      return await task();
    } catch (error) {
      if (isAbortError(error) || signal?.aborted || attempt >= retries) throw error;
      await abortableDelay(baseDelayMs * 2 ** attempt, signal);
    }
  }
};

export interface ConcurrencyOptions {
  concurrency: number;
  signal?: AbortSignal;
}

export const mapWithConcurrency = async <I, O>(
  items: readonly I[],
  mapper: (item: I, index: number) => Promise<O>,
  { concurrency, signal }: ConcurrencyOptions,
): Promise<PromiseSettledResult<O>[]> => {
  const results = new Array<PromiseSettledResult<O>>(items.length);
  let cursor = 0;

  const worker = async () => {
    while (cursor < items.length) {
      throwIfAborted(signal);
      const index = cursor++;
      try {
        results[index] = { status: 'fulfilled', value: await mapper(items[index], index) };
      } catch (reason) {
        if (isAbortError(reason)) throw reason;
        results[index] = { status: 'rejected', reason };
      }
    }
  };

  const workerCount = Math.min(Math.max(1, concurrency), items.length);
  await Promise.all(Array.from({ length: workerCount }, worker));
  return results;
};
